import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { bannerService } from "../../../services/api/bannerService";
import { AuthContext } from "../../../contexts/AuthContext";

import CustomTable from "../components/CustomTable";
import AddBannerPopup from "./components/AddBannerPopup";
import Loading from "../components/Loading";
import Modal from "../components/Modal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { ReactComponent as AddIcon } from "../../../assets/icons/fi-plus-circle.svg";
import SearchInput from "../components/SearchInput";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

const MAX_SELECTED = 5;

function fmtDateMaybe(val) {
  if (!val) return "--";
  if (typeof val === "string" && val.includes("/")) return val;
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dd = d.toLocaleDateString("vi-VN");
    const tt = d.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dd} ${tt}`;
  } catch {
    return String(val);
  }
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

const BannerManager = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);

  const [canDelete, setCanDelete] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const search_value = searchParams.get("search_value") || "";
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 10);

  const [searchText, setSearchText] = useState(search_value);
  const [tableLoading, setTableLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);

  const [showCancelSelectModal, setShowCancelSelectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBannerId, setDeletingBannerId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [popupMode, setPopupMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [initialForm, setInitialForm] = useState(null);

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const [serverSelectedIds, setServerSelectedIds] = useState(() => new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectingIds, setSelectingIds] = useState(() => new Set());
  const [selectionDirty, setSelectionDirty] = useState(false);
  const initedPagesRef = useRef(new Set());

  const updateQuery = (next) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") p.delete(k);
      else p.set(k, String(v));
    });
    setSearchParams(p, { replace: true });
  };

  const mapApiToRows = useCallback(
    (items) =>
      (items || []).map((it, idx) => ({
        _id: it._id,
        stt: (page - 1) * limit + idx + 1,
        name: it.name || "--",
        imageUrl: it.thumbnail || null,
        creator: it.createdBy || "--",
        createdAt: fmtDateMaybe(it.createdAt),
        isMobile: !!it.isMobile,
        txtUrl: it.txtUrl || "",
      })),
    [page, limit],
  );

  const fetchList = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setTableLoading(true);
        const res = await bannerService.get_banner(token, {
          search_value,
          page,
          limit,
        });

        const payload = res?.data?.data || res?.data || {};
        const items = payload.news || [];
        const pg = payload.pagination || {};

        const mapped = mapApiToRows(items);
        setRows(mapped);
        setTotalPages(Number(pg.totalPages) || 1);

        const pageIds = mapped.map((r) => r._id).filter(Boolean);
        const pageEnabled = mapped.filter((r) => r.isMobile).map((r) => r._id);
        setServerSelectedIds((prev) => {
          const n = new Set(prev);
          pageIds.forEach((id) => n.delete(id));
          pageEnabled.forEach((id) => n.add(id));
          return n;
        });
      } catch (err) {
        toast.dismiss();
        toast.error(
          err?.response?.data?.message || "Không thể tải danh sách banner.",
        );
      } finally {
        if (!silent) setTableLoading(false);
      }
    },
    [token, search_value, page, limit, mapApiToRows],
  );

  useEffect(() => {
    fetchList();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchList, user]);

  useEffect(() => {
    if (!isSelecting) return;
    if (!rows.length) return;
    if (initedPagesRef.current.has(page)) return;

    const pageEnabled = rows.filter((r) => r.isMobile).map((r) => r._id);
    setSelectingIds((prev) => {
      const n = new Set(prev);
      pageEnabled.forEach((id) => n.add(id));
      return n;
    });
    initedPagesRef.current.add(page);
  }, [isSelecting, rows, page]);

  const {
    debouncedSearch,
    searchImmediately,
  } = useDebouncedSearch(
    (value) => updateQuery({ search_value: value, page: 1 }),
    500,
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    debouncedSearch(value);
  };

  const onChangePage = (p) => {
    if (p < 1 || p > totalPages) return;
    updateQuery({ page: p });
  };

  const onToggleSelectOne = (id) => {
    if (!isSelecting) return;

    const has = selectingIds.has(id);
    const nextSize = has ? selectingIds.size - 1 : selectingIds.size + 1;
    if (!has && nextSize > MAX_SELECTED) {
      toast.error(`Chỉ được chọn tối đa ${MAX_SELECTED} banner.`);
      return;
    }

    setSelectingIds((prev) => {
      const n = new Set(prev);
      has ? n.delete(id) : n.add(id);
      return n;
    });
    setSelectionDirty(true);
  };

  const enterSelect = () => {
    setIsSelecting(true);
    setSelectingIds(new Set(serverSelectedIds));
    setSelectionDirty(false);
    initedPagesRef.current = new Set([page]);
  };

  const cancelSelect = () => {
    const hasChanges = !setsEqual(selectingIds, serverSelectedIds);
    if (isSelecting && hasChanges) {
      setShowCancelSelectModal(true);
      return;
    }
    setIsSelecting(false);
    setSelectingIds(new Set());
    setSelectionDirty(false);
    initedPagesRef.current = new Set();
  };

  const confirmCancelSelect = () => {
    setIsSelecting(false);
    setSelectingIds(new Set());
    setSelectionDirty(false);
    initedPagesRef.current = new Set();
    setShowCancelSelectModal(false);
  };

  const saveSelection = async () => {
    if (selectingIds.size > MAX_SELECTED) {
      toast.error(
        `Chỉ được chọn tối đa ${MAX_SELECTED} banner. Vui lòng bỏ chọn bớt.`,
      );
      return;
    }
    try {
      const current = serverSelectedIds;
      const edited = selectingIds;

      const toEnable = Array.from(edited).filter((id) => !current.has(id));
      const toDisable = Array.from(current).filter((id) => !edited.has(id));

      if (!toEnable.length && !toDisable.length) {
        toast.info("Không có thay đổi để lưu.");
        confirmCancelSelect();
        return;
      }

      if (toDisable.length) {
        await bannerService.applyMobile(token, {
          newIds: toDisable,
          enable: false,
        });
      }
      if (toEnable.length) {
        await bannerService.applyMobile(token, {
          newIds: toEnable,
          enable: true,
        });
      }

      toast.success("Cập nhật danh sách hiển thị thành công!");
      setIsSelecting(false);
      setSelectingIds(new Set());
      setSelectionDirty(false);
      initedPagesRef.current = new Set();
      setServerSelectedIds(new Set(edited));
      fetchList({ silent: true });
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Không thể lưu thay đổi.");
    }
  };

  const openCreate = () => {
    setPopupMode("create");
    setEditingId(null);
    setInitialForm({ name: "", content: "", imagePreview: "" });
    setOpenPopup(true);
  };

  const handleAddPost = async (formData) => {
    try {
      await bannerService.create(token, formData);
      toast.success("Tạo banner thành công!");
      setOpenPopup(false);
      fetchList({ silent: true });
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Không thể tạo banner.");
    }
  };

  const onEditClick = async (bannerId) => {
    try {
      setEditLoading(true);
      const res = await bannerService.get_detail(token, bannerId);
      const payload = res?.data?.data || res?.data || {};

      setPopupMode("edit");
      setEditingId(bannerId);

      setInitialForm({
        name: payload.name || "",
        txtUrl: payload.txtUrl || "",
        imagePreview: payload.thumbnail || "",
      });

      setOpenPopup(true);
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Không tải được chi tiết.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditPost = async (formData) => {
    if (!editingId) return;
    try {
      await bannerService.update(token, editingId, formData);
      toast.success("Cập nhật banner thành công!");
      setOpenPopup(false);
      setRows((prev) =>
        prev.map((r) =>
          r._id === editingId
            ? {
              ...r,
              name: formData.get("name") || r.name,
            }
            : r,
        ),
      );
      fetchList({ silent: true });
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Không thể cập nhật banner.");
    }
  };

  const handleDeletePost = async (banner_id) => {
    if (!banner_id) return;
    setDeletingBannerId(banner_id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingBannerId) return;

    const prevRows = rows;
    const prevServer = new Set(serverSelectedIds);
    const prevSelecting = new Set(selectingIds);
    const wasSelecting = isSelecting;

    setIsDeleting(true);

    try {
      setRows((prev) => prev.filter((r) => r._id !== deletingBannerId));
      setServerSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(deletingBannerId);
        return n;
      });
      if (isSelecting) {
        setSelectingIds((prev) => {
          const n = new Set(prev);
          n.delete(deletingBannerId);
          return n;
        });
      }

      await bannerService.delete(token, deletingBannerId);
      toast.success("Xoá banner thành công!");
      setShowDeleteModal(false);
      setDeletingBannerId(null);
      fetchList({ silent: true });
    } catch (err) {
      setRows(prevRows);
      setServerSelectedIds(prevServer);
      if (wasSelecting) setSelectingIds(prevSelecting);

      toast.dismiss();
      toast.error(err?.response?.data?.message || "Không thể xoá banner.");
    } finally {
      setIsDeleting(false);
    }
  };

  const checkboxSource = isSelecting ? selectingIds : serverSelectedIds;

  return (
    <div className="flex flex-col h-full min-h-0 gap-8 relative">
      {editLoading && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6">
            <Loading />
            <p className="mt-4 text-gray-700">Đang tải chi tiết banner...</p>
          </div>
        </div>
      )}

      <Modal
        isOpen={showCancelSelectModal}
        onClose={() => setShowCancelSelectModal(false)}
        title="Xác nhận hủy"
        maxWidth="420px"
        cancelButtonText="Quay lại"
        showCancelButton
        primaryButtonText="Hủy thay đổi"
        primaryButtonVariant="danger"
        primaryButtonOnClick={confirmCancelSelect}
      >
        <div className="px-6 py-4">
          <p className="text-gray-700">
            Những thay đổi của bạn sẽ không được lưu. Bạn có muốn hủy?
          </p>
        </div>
      </Modal>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingBannerId(null);
        }}
        onConfirm={confirmDelete}
        title="Xác nhận xóa banner"
        message="Bạn có chắc muốn xoá banner này?"
        loading={isDeleting}
      />

      <div className="shrink-0 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="w-full md:max-w-[520px]">
          <SearchInput
            value={searchText}
            onChange={handleSearchChange}
            onSearch={(value) => searchImmediately(value)}
            placeholder="Tìm kiếm banner"
          />
        </div>

        <div className="shrink-0 flex flex-row items-center gap-6">
          {isSelecting && (
            <span className="text-sm text-gray-600 mr-1">
              Đã chọn {selectingIds.size} / {MAX_SELECTED}
            </span>
          )}

          <button
            onClick={openCreate}
            className="
              h-[42px]
              px-4
              bg-[#0457C2]
              text-white
              rounded-lg
              text-sm
              font-medium
              flex items-center justify-center gap-2
              hover:bg-[#0348a3]
              active:scale-[0.99]
              transition-all
              whitespace-nowrap
            "
          >
            <AddIcon className="w-4 h-4" />
            Thêm mới
          </button>

          {!isSelecting ? (
            <button
              onClick={enterSelect}
              className="h-[40px] hover:bg-gray-100 hover:text-[#033678] bg-white border text-[#0457C2] border-[#0457C2] px-4 py-2 rounded-lg text-sm font-be-vietnam-medium flex items-center justify-center gap-2"
            >
              Chỉnh sửa danh sách hiển thị
            </button>
          ) : (
            <>
              <button
                onClick={saveSelection}
                className="h-[40px] px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 text-sm font-be-vietnam-medium"
                disabled={selectingIds.size > MAX_SELECTED}
                title={
                  selectingIds.size > MAX_SELECTED
                    ? `Tối đa ${MAX_SELECTED} banner`
                    : undefined
                }
              >
                Lưu danh sách hiển thị
              </button>
              <button
                onClick={cancelSelect}
                className="h-[40px] hover:bg-gray-300 hover:text-white border text-gray-600 border-gray-300 px-4 py-2 rounded-lg text-sm font-be-vietnam-medium flex items-center justify-center gap-2"
              >
                Hủy
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-visible">
        <div className="h-full min-h-0 overflow-auto p-[2px]">
          <CustomTable
            columns={[
              { key: "stt", title: "STT", align: "text-left" },
              {
                key: "imageUrl",
                title: "Hình ảnh",
                minWidth: "140px",
                render: (v) => (
                  <div className="h-[6.5rem] rounded-xl">
                    {v ? (
                      <img
                        src={v}
                        alt="banner"
                        className="h-full object-cover w-full object-center shrink-0"
                      />
                    ) : (
                      <div className="grid place-items-center w-full h-full text-xs text-gray-400">
                        No image
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: "name",
                title: "Tên banner",
                minWidth: "350px",
                render: (val) => <span className="truncate">{val}</span>,
              },
              { key: "creator", title: "Người tạo", minWidth: "160px" },
              { key: "createdAt", title: "Thời gian tạo", minWidth: "180px" },
            ]}
            data={rows}
            currentPage={page}
            setCurrentPage={onChangePage}
            totalPages={totalPages}
            onRowClick={(data) => navigate(`/quan-ly-banner/banner/${data._id}`)}
            setEdit
            handleEdit={(id) => onEditClick(id)}
            setDelete={canDelete}
            handleDelete={handleDeletePost}
            basePath="/quan-ly-banner/banner"
            selectable
            selectedIds={checkboxSource}
            selectionDisabled={!isSelecting}
            rowIdKey="_id"
            onToggleRow={onToggleSelectOne}
            loading={tableLoading}
            loadingText="Đang tải danh sách banner..."
          />
        </div>
      </div>

      {openPopup && (
        <AddBannerPopup
          mode={popupMode}
          initial={initialForm}
          closePopup={() => setOpenPopup(false)}
          handleAddPost={handleAddPost}
          handleEditPost={handleEditPost}
        />
      )}
    </div>
  );
};

export default BannerManager;
